/**
 * Routing-Policy API service for the Whakahaere Safety Management System.
 *
 * Backs the ADR-126 Decision 4 per-tenant `RoutingPolicy` editor:
 * a `(alert, site-operating-state) -> routing adjustment` mapping that the
 * Notification Delivery Manager eligibility gate AND-s with the rubric and the
 * on-duty cache. Attaches at the org (district) or site (school) tier with
 * org→site inheritance, mirroring `SiteOperatingCalendar`.
 *
 * Typed `@tiaki/http-client` surface per ADR-113. The backend `routing_policy`
 * app (`RoutingPolicy` + gate + REST layer) is in the generated OpenAPI schema,
 * so the resource shape and call sites come straight off
 * `components['schemas']` and `client.GET/POST/PATCH/DELETE` — no `client.ky`
 * escape hatch and no hand-written shapes to drift.
 *
 * Contract note: the list endpoints are unpaginated (the viewset declares no
 * `pagination_class`), so `list` / `for-school` return a bare array, not a
 * `{count,next,previous,results}` envelope.
 */

import type { components } from '@tiaki/http-client/types';
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// ---------------------------------------------------------------------------
// Enums — re-exported from the generated schema (mirror
// server.apps.routing_policy.models). The label maps stay app-owned.
// ---------------------------------------------------------------------------

/** Which resolved site-operating state arms a `RoutingPolicy`. */
export type OperatingCondition = components['schemas']['WhenStateEnum'];

export const OPERATING_CONDITION_LABELS: Record<OperatingCondition, string> = {
  not_operating: 'When the site is NOT operating',
  operating: 'When the site IS operating',
  any: 'Regardless of operating state',
};

/**
 * Which resolved scheduled-event (drill/training) state arms a `RoutingPolicy`.
 * AND-ed with `when_state`: a policy is armed only when BOTH the
 * operating-state and the scheduled-event predicates match. The three aggregate
 * values key on event *presence*; the per-type leaves reuse `SiteEventType`
 * (`drill` / `training` / `field_exercise` / `other`) verbatim.
 */
export type WhenEvent = components['schemas']['WhenEventEnum'];

export const WHEN_EVENT_LABELS: Record<WhenEvent, string> = {
  any: 'Regardless of scheduled events',
  no_event: 'Only when NO scheduled event is active',
  any_event: 'When ANY scheduled event is active',
  drill: 'Only during a drill',
  training: 'Only during a training',
  field_exercise: 'Only during a field exercise',
  other: "Only during an 'other' scheduled event",
};

/** What a matched `RoutingPolicy` does to the rubric recipient set (narrow only). */
export type RoutingAdjustment = components['schemas']['AdjustmentEnum'];

export const ROUTING_ADJUSTMENT_LABELS: Record<RoutingAdjustment, string> = {
  no_adjustment: 'No adjustment (rubric set unchanged)',
  restrict_to_on_duty: 'Restrict to on-duty / on-call recipients',
  suppress_all: 'Suppress the entire fan-out',
};

// ---------------------------------------------------------------------------
// Resource shape — sourced from the generated OpenAPI components.
// ---------------------------------------------------------------------------

/**
 * A per-tenant `(alert, site-operating-state) -> routing adjustment` rule.
 * Exactly one of `school` / `district` is set: `school` => site-level
 * (overrides the org default for that site); `district` => org-level default.
 */
export type RoutingPolicy = components['schemas']['RoutingPolicy'];

export interface RoutingPolicyFilters {
  district?: number;
  school?: number;
  alert_type?: number;
  is_active?: boolean;
  ordering?: string;
}

export type RoutingPolicyWriteInput =
  components['schemas']['RoutingPolicyRequest'];

/**
 * List routing policies. NB: unpaginated — returns a bare array
 * (the `routing_policy` viewset declares no `pagination_class`).
 */
export async function getRoutingPolicies(
  filters: RoutingPolicyFilters = {},
): Promise<RoutingPolicy[]> {
  const result = await client.GET('/api/v2/routing-policies/', {
    params: { query: filters },
  });
  return unwrap(result);
}

export async function getRoutingPolicyById(id: number): Promise<RoutingPolicy> {
  const result = await client.GET('/api/v2/routing-policies/{id}/', {
    params: { path: { id } },
  });
  return unwrap(result);
}

/**
 * Policies that govern a site — its own rows plus the org defaults — so the UI
 * can show the inherited org default alongside any site override.
 */
export async function getRoutingPoliciesForSchool(
  schoolId: number,
): Promise<RoutingPolicy[]> {
  const result = await client.GET('/api/v2/routing-policies/for-school/', {
    params: { query: { school: schoolId } },
  });
  return unwrap(result);
}

export async function createRoutingPolicy(
  data: RoutingPolicyWriteInput,
): Promise<RoutingPolicy> {
  const result = await client.POST('/api/v2/routing-policies/', { body: data });
  return unwrap(result);
}

export async function updateRoutingPolicy(
  id: number,
  data: Partial<RoutingPolicyWriteInput>,
): Promise<RoutingPolicy> {
  const result = await client.PATCH('/api/v2/routing-policies/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteRoutingPolicy(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/routing-policies/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}
